import { useState } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { Button } from '../components/ui/button';
import { Code, FilePdf, Spinner, CaretDown, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API_DOCS } from './docsContent';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const METHOD_TONE = {
  GET:    'bg-emerald-50  text-emerald-700  ring-emerald-200',
  POST:   'bg-sky-50      text-sky-700      ring-sky-200',
  PUT:    'bg-amber-50    text-amber-700    ring-amber-200',
  PATCH:  'bg-violet-50   text-violet-700   ring-violet-200',
  DELETE: 'bg-rose-50     text-rose-700     ring-rose-200',
};

export default function DocumentationPage() {
  const [downloading, setDownloading] = useState(false);

  const handleExport = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/docs/api/pdf`, {
        headers: authHeaders(),
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ecowater-api-docs.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to export PDF'));
    } finally { setDownloading(false); }
  };

  return (
    <div className="space-y-4 pb-8" data-testid="documentation-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Documentation
          </h1>
          <p className="text-[#78716C] mt-1 text-sm" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            API reference for the Ecowater Cloud control plane.
          </p>
        </div>
        <Button
          onClick={handleExport}
          disabled={downloading}
          className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white"
          data-testid="docs-export-pdf"
        >
          {downloading ? <Spinner size={14} className="animate-spin mr-2" /> : <FilePdf size={14} className="mr-2" />}
          {downloading ? 'Exporting...' : 'Export PDF'}
        </Button>
      </div>

      <div className="rounded-2xl border border-[#EDE6D8] bg-[#FCFAF6]/30 p-4 sm:p-6">
        <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5 mb-5">
          <div className="flex items-center gap-2 mb-2">
            <Code size={18} weight="duotone" className="text-[#0A2540]" />
            <h2 className="text-xl font-bold text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {API_DOCS.title}
            </h2>
          </div>
          <p className="text-sm text-[#78716C] leading-relaxed">{API_DOCS.intro}</p>
        </div>

        <div className="space-y-6">
          {API_DOCS.sections.map((section, idx) => (
            <Section key={idx} section={section} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Section({ section }) {
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
      <h3 className="text-base font-bold text-[#0A2540]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        {section.heading}
      </h3>
      {section.body && (
        <p className="text-sm text-[#78716C] leading-relaxed mt-1 mb-4">{section.body}</p>
      )}
      <div className="space-y-2">
        {section.endpoints.map((ep, i) => (
          <EndpointRow key={`${ep.method}-${ep.path}-${i}`} ep={ep} />
        ))}
      </div>
    </div>
  );
}

function EndpointRow({ ep }) {
  const [open, setOpen] = useState(false);
  const tone = METHOD_TONE[ep.method] || METHOD_TONE.GET;

  return (
    <div className={`rounded-lg border ${open ? 'border-[#0A2540]/30 bg-white' : 'border-[#EDE6D8] bg-[#FCFAF6]/40'} transition-colors`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-3 py-2 text-left"
        data-testid={`endpoint-${ep.method.toLowerCase()}-${ep.path.replace(/[^a-z0-9]/gi, '-')}`}
        aria-expanded={open}
      >
        <span
          className={`shrink-0 inline-flex items-center justify-center w-16 px-2 py-0.5 text-[10px] font-bold rounded-md ring-1 ring-inset ${tone}`}
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
        >
          {ep.method}
        </span>
        <code className="text-xs text-[#1C1917] font-semibold flex-shrink min-w-0 truncate" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {ep.path}
        </code>
        <span className="text-xs text-[#78716C] flex-1 text-right truncate hidden sm:inline">
          {ep.summary}
        </span>
        <CaretDown
          size={14}
          className={`shrink-0 text-[#78716C] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="border-t border-[#EDE6D8] px-4 py-4 space-y-4 bg-white rounded-b-lg">
          {/* On small screens the summary collapses, so show it expanded */}
          <p className="text-sm text-[#1C1917] sm:hidden">{ep.summary}</p>

          {ep.auth && (
            <div className="flex items-center gap-2 text-xs text-[#78716C]">
              <ShieldCheck size={14} weight="duotone" className="text-[#0A2540]" />
              <span className="uppercase tracking-wider font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Auth
              </span>
              <span className="text-[#1C1917] font-medium">{ep.auth}</span>
            </div>
          )}

          {ep.body && <CodeBlock label="Request Body" code={ep.body} />}
          {ep.response && <CodeBlock label="Response" code={ep.response} />}

          {ep.notes?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Notes
              </p>
              <ul className="space-y-1.5">
                {ep.notes.map((n, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#1C1917] leading-relaxed">
                    <span className="text-[#C9A35B] font-bold mt-0.5 shrink-0">·</span>
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CodeBlock({ label, code }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold mb-1.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        {label}
      </p>
      <pre
        className="text-[11px] leading-snug bg-[#0A2540] text-[#FCFAF6] rounded-lg px-3.5 py-3 overflow-x-auto"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
      >
{code}
      </pre>
    </div>
  );
}
